import { PrepaymentProvider } from "../../../../../common/enums";
import { CUSTOM_FORM_LINK_OPTION_ID } from "../../../../../common/preReservationForms/constants";

/**
 * The subset of the class wizard form that has "switch + details" semantics. Everything
 * is optional because the yup-inferred form type is; an absent switch counts as off.
 */
export interface IClassWizardOptionFields {
  requirePrepayment?: boolean;
  prepaymentProvider?: PrepaymentProvider | null;
  prepaymentLink?: string | null;
  requireFormCompletion?: boolean;
  preReservationFormId?: number | null;
  formCompletionLink?: string | null;
  isMultisportAvailable?: boolean;
  hasCustomMultisportPrice?: boolean;
  multisportCustomPrice?: number | null;
}

export interface INormalizedClassWizardOptions {
  prepaymentProvider: PrepaymentProvider | null;
  prepaymentLink: string | null;
  preReservationFormId: number | null;
  formCompletionLink: string | null;
  multisportCustomPrice: number | null;
}

/**
 * Reconciles the class options before they go to the API. Each "require" switch owns
 * its detail fields: off wipes them, on never persists mutually exclusive details
 * together. Mirrors EventOptionsNormalizer on the backend.
 *
 * Without the prepayment part a class that once used Stripe kept its provider after
 * prepayment was switched off, and the portal opened a Stripe checkout for a class
 * nobody had to pay for.
 */
export const normalizeClassWizardOptions = (
  data: IClassWizardOptionFields,
): INormalizedClassWizardOptions => {
  const requirePrepayment = data.requirePrepayment === true;
  const requireFormCompletion = data.requireFormCompletion === true;

  const prepaymentProvider = requirePrepayment
    ? data.prepaymentProvider ?? null
    : null;

  const prepaymentLink =
    requirePrepayment && prepaymentProvider === PrepaymentProvider.Link
      ? data.prepaymentLink ?? null
      : null;

  // The dropdown carries either an in-app form id, the custom-link sentinel, or nothing.
  const usingInAppForm =
    requireFormCompletion &&
    data.preReservationFormId != null &&
    data.preReservationFormId !== CUSTOM_FORM_LINK_OPTION_ID;

  const preReservationFormId = usingInAppForm
    ? (data.preReservationFormId as number)
    : null;

  const formCompletionLink =
    requireFormCompletion && !usingInAppForm
      ? data.formCompletionLink ?? null
      : null;

  // hasCustomMultisportPrice is a FE-only toggle; the API only cares about the value.
  const multisportCustomPrice =
    data.isMultisportAvailable === true && data.hasCustomMultisportPrice === true
      ? data.multisportCustomPrice ?? null
      : null;

  return {
    prepaymentProvider,
    prepaymentLink,
    preReservationFormId,
    formCompletionLink,
    multisportCustomPrice,
  };
};

/**
 * Label + tooltip for the "Позволи онлайн резервации" toggle.
 *
 * The toggle is shown for hidden classes too: hiding a class only removes it from the
 * calendar listing, it stays reachable through the link the studio copies from the class
 * page. The wording says which audience the switch governs, so "hidden" is never mistaken
 * for "nobody can book it".
 */
export const getAllowOnlineReservationsCopy = (showInUserPortal: boolean) =>
  showInUserPortal
    ? {
        label: "Позволи онлайн резервации",
        tooltip:
          "Когато тази опция е изключена, потребителите ще виждат класа в календара, но няма да могат да резервират онлайн. Вместо това ще виждат съобщение да се свържат със обекта.",
      }
    : {
        label: "Позволи онлайн резервации (само през линк)",
        tooltip:
          "Класът не се вижда в календара, но остава достъпен през копиран линк. Когато тази опция е изключена, отворилите линка ще виждат класа, но няма да могат да резервират онлайн.",
      };

/**
 * Tooltip for the "Инструктор" select. Its second sentence is about the instructor's
 * commission, so a studio that does not use commissions gets only the first one.
 */
export const getInstructorTooltip = (studioHidesCommissions: boolean) =>
  studioHidesCommissions
    ? "Инструкторът, който ще води класа. Важно е, за да може да се генерират правилни репорти."
    : "Инструкторът, който ще води класа. Важно е, за да може да се генерират правилни репорти.<br/><br/>Ако не изберете инструктор, няма да бъде изчислен автоматично комисион % за нито един служител.";
